/**
 * Date de "mise à jour" trimestrielle dynamique.
 *
 * Règle (demandée) : la première mise à jour affichée correspond à aujourd'hui
 * (au moment du build/render), puis cette date avance automatiquement tous
 * les 3 mois (90 jours).
 *
 * Implémentation :
 *   - On part d'une ancre de référence (FIRST_REFRESH_ANCHOR) = première fois
 *     où ce système a été mis en place.
 *   - On calcule combien de fenêtres de 90 jours se sont écoulées depuis.
 *   - L'updatedAt affiché = ancre + N × 90 jours, borné à aujourd'hui.
 *   - Si l'article a été publié APRÈS cette date, on garde la date de publication.
 *
 * Résultat :
 *   - Le jour J de mise en place : updatedAt = aujourd'hui.
 *   - Tous les 3 mois ensuite : la date "Mis à jour le …" avance d'un cran.
 *   - Pas besoin d'éditer les articles à la main.
 */

// Ancre fixée au déploiement de la fonctionnalité.
const FIRST_REFRESH_ANCHOR = "2026-05-13"; // YYYY-MM-DD
const QUARTER_DAYS = 90;
const MS_PER_DAY = 86_400_000;

function parseISO(d: string): Date {
  // Force UTC midi pour éviter les décalages de fuseau.
  return new Date(`${d}T12:00:00Z`);
}

function toISO(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Dernière date trimestrielle ≤ aujourd'hui, à partir de l'ancre. */
function latestQuarterlyAnchor(now: Date = new Date()): Date {
  const anchor = parseISO(FIRST_REFRESH_ANCHOR);
  const diffDays = Math.floor((now.getTime() - anchor.getTime()) / MS_PER_DAY);
  if (diffDays <= 0) return anchor;
  const steps = Math.floor(diffDays / QUARTER_DAYS);
  return new Date(anchor.getTime() + steps * QUARTER_DAYS * MS_PER_DAY);
}

/**
 * Renvoie la date de mise à jour à afficher pour un article.
 * @param publishedAt YYYY-MM-DD
 * @param explicitUpdatedAt si fourni, prend toujours le dessus.
 */
export function getDisplayUpdatedAt(
  publishedAt: string,
  explicitUpdatedAt?: string,
): string {
  if (explicitUpdatedAt) return explicitUpdatedAt;
  const pub = parseISO(publishedAt);
  const quarter = latestQuarterlyAnchor();
  const chosen = pub.getTime() > quarter.getTime() ? pub : quarter;
  return toISO(chosen);
}

/** Format français : "12 mai 2026". */
export function formatFrenchDate(iso: string): string {
  try {
    return parseISO(iso).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}
